/**
 * Helpers for opening a record's original source file (Phase 5 traceability).
 * The bytes are fetched authenticated and handed to the browser; for a PDF we try
 * to jump straight to the cited page, so "open source" lands the reviewer where
 * the value was read rather than on page 1.
 */

/** MIME types a browser renders inline (PDF / any image); everything else downloads. */
const INLINE_VIEWABLE_PATTERN = /^(application\/pdf|image\/)/;

/**
 * Reads a 1-based page number from a record's extraction payload, for the PDF
 * viewer's `#page=N` deep-link. Prefers an explicit `page` field; falls back to a
 * `p.12`-style page ref carried in `cell` (the non-Excel provenance convention).
 * Returns null when no page is present (Excel cells, slides, images).
 * @param {Record<string, unknown>} rawPayload The record's original extraction payload.
 * @returns {number | null} The 1-based page number, or null when absent.
 */
export function readPageHint(rawPayload: Record<string, unknown>): number | null {
  const page = readPositiveInteger(rawPayload.page);
  if (page !== null) {
    return page;
  }
  const cell = rawPayload.cell;
  if (typeof cell === 'string') {
    const match = /p\.?\s*(\d+)/i.exec(cell);
    if (match !== null) {
      return Number(match[1]);
    }
  }
  return null;
}

/** Reads a positive integer from a payload entry that may be a number or a numeric string. */
function readPositiveInteger(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
    return value;
  }
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    const parsed = Number(value.trim());
    return parsed > 0 ? parsed : null;
  }
  return null;
}

/**
 * Reads a 1-based slide number from a record's extraction payload, for the deck
 * viewer. Prefers the explicit `slide` field, then a `slide 3`-style ref carried in
 * `cell` (slide prose), then the synthetic worksheet name a deck TABLE was captured
 * under (`Slide 3 table 1`) — those records look like spreadsheet records but their
 * document is the deck. Returns null when the record cites no slide.
 * @param {Record<string, unknown>} rawPayload The record's original extraction payload.
 * @returns {number | null} The 1-based slide number, or null when absent.
 */
export function readSlideHint(rawPayload: Record<string, unknown>): number | null {
  const slide = readPositiveInteger(rawPayload.slide);
  if (slide !== null) {
    return slide;
  }
  const cell = rawPayload.cell;
  if (typeof cell === 'string') {
    const match = /slide\s*(\d+)/i.exec(cell);
    if (match !== null) {
      return Number(match[1]);
    }
  }
  const sheet = rawPayload.sheet;
  if (typeof sheet === 'string') {
    const match = /^\s*slide\s+(\d+)\b/i.exec(sheet);
    if (match !== null) {
      return Number(match[1]);
    }
  }
  return null;
}

/** True when the browser can render this MIME type inline (PDF/image). */
export function isInlineViewable(mimeType: string): boolean {
  return INLINE_VIEWABLE_PATTERN.test(mimeType);
}
